import * as React from "react";
//#region src/components/overlay-layering.ts
/**
* Tracks how many overlay surfaces already wrap the current subtree.
*
* Dialogs, alert dialogs, and drawers all consume this shared depth so each
* child overlay can render its backdrop/popup pair above the parent surface
* without every primitive maintaining a separate z-index ladder.
*/
const OverlayStackContext = React.createContext(0);
/**
* Lets a child overlay notify its parent surface that it is currently open.
*
* This is separate from `OverlayStackContext`: z-index layering answers "how
* high should this surface render?", while this registration channel answers
* "should the parent surface draw its nested-overlay tint right now?".
*/
const OverlaySurfaceContext = React.createContext(null);
/**
* Backdrop z-index for a top-level overlay surface.
*/
const OVERLAY_Z_INDEX_BASE = 100010;
/**
* Distance reserved for each overlay layer.
*
* Each layer uses one slot for the backdrop and the next slot for the popup,
* leaving headroom between layers for future internal stacking needs.
*/
const OVERLAY_Z_INDEX_STEP = 10;
/**
* Computes the backdrop and popup z-index pair for a given overlay depth.
*
* @param depth - Number of ancestor overlay surfaces wrapping the current one
* @returns Backdrop and popup z-index values for the current overlay layer
*/
function getOverlayLayerZIndex(depth) {
	const overlay = OVERLAY_Z_INDEX_BASE + depth * OVERLAY_Z_INDEX_STEP;
	return {
		overlay,
		popup: overlay + 1
	};
}
/**
* Tracks whether the current overlay surface has an open child overlay.
*
* Registration is tied to the mounted popup element instead of the caller's
* JSX presence, so closed dialogs/drawers that remain in the React tree do
* not incorrectly tint their parent surface.
*
* @param isOpen - Whether the current overlay popup is mounted and visible
* @returns Parent-tint state and a provider value for descendant overlays
*/
function useOverlaySurfaceState(isOpen) {
	const registerWithParent = React.useContext(OverlaySurfaceContext);
	const [nestedOverlayCount, setNestedOverlayCount] = React.useState(0);
	React.useEffect(() => {
		if (!isOpen || !registerWithParent) return;
		return registerWithParent();
	}, [isOpen, registerWithParent]);
	const registerNestedOverlay = React.useCallback(() => {
		setNestedOverlayCount((count) => count + 1);
		return () => {
			setNestedOverlayCount((count) => count - 1);
		};
	}, []);
	return {
		hasNestedOverlay: nestedOverlayCount > 0,
		registerNestedOverlay
	};
}
//#endregion
export { OverlayStackContext, OverlaySurfaceContext, getOverlayLayerZIndex, useOverlaySurfaceState };

//# sourceMappingURL=overlay-layering.js.map